import Vue from 'vue'
import { Module } from 'vuex'

import { ChatRoom, User_ChatRoomView, Profile, Channel, MessageSummary, ChatRoomProps, MesgFilterInfo } from '@dinosband/bv-main-schema/dist/types'
import { ChatRoomLastMessageInfo, Message } from '@dinosband/bv-messenger-schema'
import { AttachmentBroadcast, AttachmentState } from '@dinosband/bv-attachment'
import { types } from '@dinosband/bv-main-client/src/types'
import * as mesgProcess from '@dinosband/bv-main-client/src/mesg-process'
import { mainStore } from '@dinosband/bv-utils'

import { MessengerState } from '..'

// export interface ChatRoomLastMessage {
//   id: number
//   seq: number
//   message?: Message
// }

export interface ChatRoomLastRead {
  id: number
  seq: number
}

export interface ChatRoomState {
  chatRoom: ChatRoom
  channel: Channel | null
  userProfile: Profile
  userView: User_ChatRoomView
  profiles: Profile[]
  props?: ChatRoomProps
  messages: Message[]
  messageSummaries: Record<number, MessageSummary> // number is messageId
  commentsChatRoomInfos: Record<number, ChatRoomLastMessageInfo> // number is chatRoomId
  firstMessageId: number | undefined
  lastMessage: Message | undefined
  filterInfo: MesgFilterInfo | undefined
  // filterEnabled?: boolean
  // lastFilteredMessage: Message | null
  lastRead: ChatRoomLastRead
  unreadCount: number
  rootMessage: Message | undefined
  rootChatRoom: ChatRoom | undefined
  isMyJobOffer: boolean
  jobOffer: types.JobOffer | undefined
  jobOfferResponse?: types.JobOfferResponse
  usersReadMessageIds: Record<number, number>
  uploadingAttachments: AttachmentBroadcast[]
  pushNotificationId: number | null
  processManager: mesgProcess.ProcessManager
}

export const ChatRoomModule: Module<ChatRoomState, MessengerState> = {
  namespaced: true,
  state () {
    return {
      chatRoom: undefined as unknown as ChatRoom,
      channel: null,
      userProfile: undefined as unknown as Profile,
      userView: undefined as unknown as User_ChatRoomView,
      profiles: [],
      messages: [],
      messageSummaries: {},
      commentsChatRoomInfos: {},
      firstMessageId: undefined,
      lastMessage: undefined,
      filterInfo: undefined,
      // lastFilteredMessage: null,
      lastRead: undefined as unknown as ChatRoomLastRead,
      unreadCount: 0,
      rootMessage: undefined,
      rootChatRoom: undefined,
      isMyJobOffer: false,
      jobOffer: undefined,
      jobOfferResponse: undefined,
      usersReadMessageIds: {},
      uploadingAttachments: [],
      pushNotificationId: null,
      processManager: undefined as unknown as mesgProcess.ProcessManager,
    }
  },
  getters: {
    // lastMessage: (state) => (chatRoomId: number) => state.lastMessages[chatRoomId]?.lastMessage,
    unreadCount: (state) => () => state.unreadCount,
  },
  mutations: {
    setChatRoom (state, chatRoom: ChatRoom) {
      state.chatRoom = chatRoom
      if (!chatRoom.rootMessageId) {
        // initialize processManager here with this chatRoom when this is public chatRoom
        // initialize it in setRootMessage when this is private chatRoom
        if (chatRoom.type === types.ChatRoomType.JOB_OFFER) {
          state.processManager = new mesgProcess.ProcessManager()
          state.processManager.initialize(mainStore, chatRoom)
        }
      }
    },
    setChannel (state, channel: Channel) {
      state.channel = channel
    },
    setUserProfile (state, profile: Profile) {
      state.userProfile = profile
    },
    setUserView (state, userView: User_ChatRoomView | undefined) {
      if (!userView) {
        return
      }
      state.userView = { ...userView }
      if (userView.lastMessageId && userView.lastMessageSeq) {
        state.lastRead = { id: userView.lastMessageId, seq: userView.lastMessageSeq }
      }
    },
    setProfiles (state, profiles: Profile[]) {
      state.profiles = profiles
    },
    setProps (state, props: ChatRoomProps) {
      state.props = props
    },
    setMessages (state, messages: Message[]) {
      state.messages = messages
    },
    setRootMessage (state, message: Message) {
      state.rootMessage = message
      state.processManager = new mesgProcess.ProcessManager()
      state.processManager.initialize(mainStore, message.chatRoomId)
    },
    setRootChatRoom (state, chatRoom: ChatRoom) {
      state.rootChatRoom = chatRoom
    },
    setIsMyJobOffer (state, isMyJobOffer: boolean) {
      state.isMyJobOffer = isMyJobOffer
    },
    setJobOffer (state, jobOffer: types.JobOffer) {
      state.jobOffer = jobOffer
    },
    setJobOfferResponse (state, response: types.JobOfferResponse) {
      state.jobOfferResponse = response
    },
    setLastMessage (state, message: Message | undefined) {
      state.lastMessage = message ? { ...message } : undefined
    },
    setUnreadCount (state, unreadCount: number) {
      state.unreadCount = unreadCount
    },
    // setLastFilteredMessage (state, message: Message) {
    //   state.lastFilteredMessage = { ...message }
    // },
    setFilterInfo (state, filterInfo: MesgFilterInfo | undefined) {
      const copied = filterInfo ? JSON.parse(JSON.stringify(filterInfo)) : undefined
      state.filterInfo = copied
    },
    // setFilterEnabled (state, enabled: boolean | undefined) {
    //   state.filterEnabled = enabled
    // },
    setFirstMessageId (state, firstMessageId: number | undefined) {
      state.firstMessageId = firstMessageId
    },
    setPushNotificationId (state, pushNotificationId: number) {
      state.pushNotificationId = pushNotificationId
    },
    setLastRead (state, lastRead: ChatRoomLastRead) {
      state.lastRead = { ...lastRead }
    },
    addMessageSummaries (state, summaries: MessageSummary[]) {
      summaries.forEach((i) => {
        Vue.set(state.messageSummaries, i.id, i)
      })
    },
    clearMessageSummaries (state) {
      state.messageSummaries = {}
    },
    addCommentsChatRoomInfos (state, infos: ChatRoomLastMessageInfo[]) {
      infos.forEach((i) => {
        Vue.set(state.commentsChatRoomInfos, i.chatRoomId, i)
      })
    },
    clearCommentsChatRoomInfos (state) {
      state.commentsChatRoomInfos = {}
    },
    updateUserReadMessageId (state, userView: User_ChatRoomView) {
      const messageId = userView.lastMessageId ?? 0
      state.usersReadMessageIds = { ...state.usersReadMessageIds, [userView.userId]: messageId }
    },
    updateUploadingAttachment (state, attachment: AttachmentBroadcast) {
      state.uploadingAttachments = state.uploadingAttachments.filter((i)=> i.url = attachment.url)
      if (attachment.state !== AttachmentState.UPLOADED) {
        state.uploadingAttachments = [...state.uploadingAttachments, attachment]
      }
    },
    reset (state) {
      state.messages = []
      state.lastMessage = undefined
      state.firstMessageId = undefined
      state.messageSummaries = {}
    },
  },
  // actions: {
  //   resetReadingState: (context) => {
  //     context.state.firstReadingMessageId = null
  //   },
  // },
}

export default ChatRoomModule